import React, { memo, useEffect } from 'react';
import classnames from 'classnames';
// @ts-ignore
import { merge } from 'lodash-es';
import * as echarts from 'echarts/core';
import { BarChart, LineChart } from 'echarts/charts';
import {
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  LegendComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import useChart from '@gp/hooks/echarts/useChart';
import useChartCustomLegend from '@gp/hooks/useChartCustomLegend';
import GeneralLegend from '@gp/components/GeneralLegendNew';
import { getDoubleYAxisBarLineChartSerieMetadatasOfRevenueCompletionOrProfitCompletion } from '../../Tabs/Finance/Details/utils';
import {
  getChartDynamicOptionData,
  getChartInitialOptionDataAndInitialLegendSelected,
} from './utils';
import {
  SERIE_NAMES_MAP,
  SERIE_ICONS_MAP,
  SERIE_LEGENDS,
} from '@gp/constants/budgetActualOrForecastCompletionEChart';
import {
  PRIMARY_RECT_DECAL_OPTIONS,
} from '@gp/constants/echarts';
import './index.scss';

echarts.use([
  BarChart,
  LineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DatasetComponent,
  CanvasRenderer,
]);

interface IDoubleYAxisBarLineChartProps extends IBaseProps {
  // 源数据
  data: any[];
  // echart serie name 映射集
  serieNamesMap?: {
    [key: string]: string;
  };
  // echart serie icon 映射集
  serieIconsMap?: {
    [key: string]: string;
  };
  // echarts 自定义图例配置
  serieLegends?: IGeneralLegend[];
  serieMetadatas?: ISerieMetadataOfDoubleYAxisBarLine[];
}

const DoubleYAxisBarLineChart: React.FC<IDoubleYAxisBarLineChartProps> = (
  props,
) => {
  const {
    data,
    serieNamesMap = SERIE_NAMES_MAP,
    serieIconsMap = SERIE_ICONS_MAP,
    serieLegends = SERIE_LEGENDS,
    serieMetadatas = getDoubleYAxisBarLineChartSerieMetadatasOfRevenueCompletionOrProfitCompletion(),
    style,
    className,
    locale = 'en',
    ...remains
  } = props;

  /**
   * 获取echarts的初始化配置（静态配置，不依赖数据动态更新的配置）、和初始化图例选中状态集
   */
  const { initialLegendSelected, initialOption } =
    getChartInitialOptionDataAndInitialLegendSelected({
      serieNamesMap,
      serieIconsMap,
      serieMetadatas,
    });

  /**
   * 1、初始化chart实例
   * 2、监听window的resize事件，处理echarts resize
   * 3、echart配置项变化时,更新echart UI
   */
  const { chartNode, option, setOption } = useChart({
    initialOption,
    locale,
  });

  const {
    // 自定义图例 选中状态集
    legendSelected,
    // 自定义图例 选中change回调
    handleLegendChange,
  } = useChartCustomLegend({
    initialLegendSelected,
    option,
    setOption,
  });

  // 数据变化时,更新echart配置项
  useEffect(() => {
    if (!Array.isArray(data)) return;
    const { sources, xAxisData } = getChartDynamicOptionData(data);
    const newOption = merge({}, option, {
      xAxis: {
        ...option?.xAxis,
        data: Array.isArray(xAxisData) ? xAxisData : [],
      },
      dataset: {
        ...option?.dataset,
        source: Array.isArray(sources) ? sources : [],
      },
    });
    // 处理Actual/Forecast柱子区别，Forecast透明度40%，每个series添加data数据，至此不用dataset中的数据
    if (Array.isArray(data) && Array.isArray(newOption?.series)) {
      for (let index = 0, len = newOption?.series?.length; index < len; index ++) {
        const item = newOption?.series?.[index];
        let dataList = [];
        for(let i = 0, l = data?.length; i < l; i ++) {
          const t = data?.[i];
          const isForecastBar = item?.type === 'bar' && item?.name === 'af' && t?.['actual_or_forecast'] === 'forecast';
          const newItem: any = {
            value: t?.[item?.name] || null,
            data: t || null,
            itemStyle: {
              color: isForecastBar ? '#5086F3' : null,
              opacity: isForecastBar
                // 柱状图forecast数据柱子透明度40%
                ? 0.4
                : item?.type === 'line'
                  // 折线图时，前后两个点没有数据且当前点有数据时，当前数据点显示出来
                  ? t?.[item?.name] && !data?.[i - 1]?.[item?.name] && !data?.[i + 1]?.[item?.name] ? 1 : 0
                  : 1,
              decal: isForecastBar ? PRIMARY_RECT_DECAL_OPTIONS : null,
            },
          };
          dataList.push(newItem);
        }
        if (dataList?.length) {
          item.data = dataList;
        }
      }
    }
    setOption?.(newOption);
  }, [data]);

  return (
    <div
      {...remains}
      style={style}
      className={classnames('gp-studio-overall-chart-comp', className)}
    >
      <div ref={chartNode} className="gp-studio-overall-chart-node"></div>
      <GeneralLegend
        options={serieLegends}
        value={legendSelected}
        onChange={handleLegendChange}
      />
    </div>
  );
};

export default memo(DoubleYAxisBarLineChart);
